import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAuthToken } from '../../TokenContext';

/**
 * Fetches DORA metrics from the existing backend endpoints and
 * returns a normalised summary object for the Control Plane.
 */
export function useDoraSummary() {
  const token = useAuthToken();
  const { user } = useSelector((s) => s.auth);
  const [dora, setDora] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token || !user?.data?.length) return;

    const fetchDora = async () => {
      setLoading(true);
      try {
        const userType = (user.data[0].type || "").toLowerCase();
        const isStakeholder = userType === 'stakeholder';

        const endpoint = isStakeholder
          ? process.env.REACT_APP_STAKEHOLDER_JDORAMETRICSSTAKEHOLDER
          : process.env.REACT_APP_CLIENT_DORAMETRICSMANAGER;

        if (!endpoint) {
          setDora({ health: 80, deployFreq: '4.2', changeFailRate: '6.1', leadTime: '2.8', mttr: '47' });
          return;
        }

        let orgCode = user.data[0].client_code;
        if (orgCode && !orgCode.includes('#')) orgCode = `CLIENT#${orgCode}`;

        const payload = isStakeholder
          ? { org_code: orgCode }
          : { manager_id: user.data[0].emp_id, pod_id: [] };

        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json', 
            Authorization: isStakeholder ? token : `Bearer ${token}` 
          },
          body: JSON.stringify(payload),
        });

        const json = await res.json();
        const raw = typeof json.body === 'string' ? JSON.parse(json.body || "{}") : json.body || {};

        const findMetric = (name) => {
          if (Array.isArray(raw)) {
            return raw.find(m => m["Metric Name"] === name);
          }
          return null;
        };

        const getVal = (name, legacyKey, def) => {
          const m = findMetric(name);
          if (m) return parseFloat(m["Metric Value"]) || 0;
          return raw[name] || raw[legacyKey] || def;
        };

        const deployFreq = getVal('Deployment Frequency', 'deploy_frequency', 4.2);
        const leadTime = getVal('Lead Time for Changes', 'lead_time', 2.8);
        const mttr = getVal('Time to Restore Service', 'mttr', 47);
        const changeFailRate = getVal('Change Failure Rate', 'change_fail_rate', 6.1);

        // Simple DORA health score (0-100): higher deploy freq + lower fail/lead/mttr = better
        const health = Math.min(100, Math.round(
          (Math.min(deployFreq, 10) / 10) * 40 +
          (1 - Math.min(changeFailRate, 100) / 100) * 30 +
          (1 - Math.min(leadTime, 30) / 30) * 20 +
          (1 - Math.min(mttr, 600) / 600) * 10
        ));

        setDora({ 
          health, 
          deployFreq: String(deployFreq), 
          changeFailRate: String(changeFailRate), 
          leadTime: String(leadTime), 
          mttr: String(mttr),
          fullMetrics: Array.isArray(raw) ? raw : []
        });
      } catch {
        setDora({ health: 80, deployFreq: '4.2', changeFailRate: '6.1', leadTime: '2.8', mttr: '47' });
      } finally {
        setLoading(false);
      }
    };

    fetchDora();
  }, [token, user]);

  return { dora, loading };
}
